/**
 * Helper (pure) ของ script ใน CardGenerateDirectScript — ส่วนที่คิด/แปลงข้อมูล ไม่ใช่ส่วน UI
 *
 * แยกออกมาเพราะทั้งสามฟังก์ชันนี้เป็นตัวตัดสินว่า "อะไรถูกส่งขึ้น backend" และ
 * "ผู้ใช้จะเห็นชื่อ run อะไร" ซึ่งเดิมฝังอยู่ใน component 5,000 บรรทัดจึงเทสต์ไม่ได้เลย
 */
import { DirectSceneItem, DirectScriptInput, FinalScriptData, ScriptWithImageItem } from "./types/MeGenTypes";
import { DEFAULT_VOICE_NAME } from "./common/megenDefaults";
import { hydrateVoicePrompt, synchronizeVoicePrompt } from "./common/voicePrompt";

/** เพดาน padding เสียงต่อ scene (วินาที) */
export const AUDIO_PADDING_MAX = 10;

/** ช่องกรอก padding → ตัวเลขที่ส่งขึ้น backend · ว่าง/ไม่ใช่ตัวเลข = undefined (ให้ backend ใช้ค่า default ของ run) */
export const parsePadding = (value: string): number | undefined => {
  if (value.trim() === "") return undefined;
  const n = parseFloat(value);
  if (Number.isNaN(n)) return undefined;
  return Math.min(AUDIO_PADDING_MAX, Math.max(0, Math.round(n * 100) / 100));
};

/** Keep an explicit empty guide in the draft request so it does not become the legacy default. */
export const buildDirectDraftItem = (item: DirectSceneItem, index: number): DirectScriptInput["items"][number] => {
  const voice = synchronizeVoicePrompt(item);
  return {
    id: String(index + 1),
    script: voice.script,
    voice_guide: voice.voice_guide,
    voice_script: voice.voice_script,
    visual: item.image_prompt ?? "",
  };
};

export type CleanScriptOptions = {
  /** ค่าจาก toggle "Generate Audio Only" */
  audioOnly: boolean;
  /** map scene id → ใช้ภาพเคลื่อนไหวไหม (state ของหน้าจอ ไม่ได้อยู่ใน script) */
  useAnimated: Record<string, boolean>;
  /** true = ใช้ padding รวมของ run → ไม่ส่ง padding ราย scene */
  useGlobalPadding: boolean;
};

/**
 * ตำแหน่งของ image_style_prompt ใน image_prompt ที่เก็บไว้แล้ว
 *
 * สูตรมาตรฐานคือ style + "\n" + scene เหมือน draft graph ของ backend
 * (app/podcast_gen/graph_script2vdo.py: image_prompt = image_style_prompt + "\n" + scene_description)
 * แต่ run ที่ generate จาก UI นี้รุ่นก่อน เก็บเป็น `scene + "\n" + style`
 * ซึ่งกลายเป็น signature ของรูป/วิดีโอใน manifest ไปแล้ว
 * การสลับลำดับจึง re-hash รูป (และวิดีโอที่ hash ต่อจาก image signature) ทั้ง run = จ่าย GPU
 * เพื่อรูปที่เหมือนกัน — จึงคงลำดับเดียวกับที่ item นั้นใช้อยู่
 */
export const imagePromptStylePosition = (storedPrompt?: string, stylePrompt?: string): "first" | "last" => {
  const stored = (storedPrompt || "").replace(/\s+/g, " ").trim();
  const style = (stylePrompt || "").replace(/\s+/g, " ").trim();
  if (!stored || !style) return "first";
  if (stored.endsWith(style) && !stored.startsWith(style)) return "last";
  return "first";
};

/**
 * `image_prompt` ที่แท้จริงของ scene = image_style_prompt + scene_description
 *
 * ช่องที่ผู้ใช้แก้ในหน้า scene คือ `scene_description` ส่วน `image_prompt` เป็นค่า derived
 * ที่ round-trip กลับมาจาก graph state (state เก็บค่าเก่าไว้) ถ้าส่งค่าที่เก็บไว้กลับไปเฉยๆ
 * การแก้ scene_description จะไม่เปลี่ยน image_prompt → ไม่เปลี่ยน image signature
 * → backend ใช้รูปเก่าแล้วตอบ regenerate_items: [] โดยไม่มี log ว่าข้ามเพราะอะไร
 * จึง derive ใหม่ทั้งตอน save และตอนเริ่ม run ให้ตรงกัน
 *
 * ข้อควรระวังสองอย่างที่ guards ข้างล่างกันไว้:
 * 1. ส่ง image_prompt ว่าง = backend ข้ามการสร้างรูปแล้ว reuse asset เดิม (graph.py: `if image_prompt or uploaded_image_url`)
 *    ซึ่งคืออาการ "แก้แล้วแต่ไม่ generate" นั่นเอง
 * 2. `scene_description` ของ run เก่าถูก backend เติมให้เท่ากับ image_prompt ทั้งก้อน
 *    (routers/script2vdo.py: `_fill_scene_description`) ถ้าประกอบซ้ำจะได้ style ซ้ำสองชั้น
 *    และ signature เปลี่ยนทั้งที่ไม่มีการแก้จริง
 */
export const composeImagePrompt = (item: {
  image_prompt?: string;
  image_style_prompt?: string;
  scene_description?: string;
}): string => {
  const style = (item.image_style_prompt || "").trim();
  const scene = (item.scene_description || "").trim();
  const stored = (item.image_prompt || "").trim();

  // ไม่มี scene (บทจากภายนอก / prompt สำเร็จรูป / draft ยังไม่เติมค่า) → คง prompt เดิม ห้ามส่งว่าง
  if (!scene) return stored;

  // prompt เดิมครอบคลุมข้อความ scene อยู่แล้ว ทั้งเคส scene ถูก backfill มาจาก image_prompt
  // และเคสผู้ใช้แก้ image_prompt ตรง (แผง Assets → POST /run/{id}/update_item) → คงของเดิม
  if (stored && stored.includes(scene)) return stored;

  // scene ถูกแก้ใหม่ → prompt ที่เก็บไว้ใช้ไม่ได้แล้ว ประกอบใหม่ด้วยลำดับเดียวกับที่ item เคยใช้
  if (!style) return scene;
  return imagePromptStylePosition(stored, style) === "last" ? `${scene}\n${style}` : `${style}\n${scene}`;
};

/**
 * ส่วนของ `image_prompt` ที่ไม่ใช่ style → ใช้ตั้ง `scene_description` กลับ เมื่อผู้ใช้แก้ image_prompt ตรง
 * เพื่อให้สอง field ไม่แตกกันอีก (ไม่งั้น run ถัดไปจะประกอบ image_prompt จาก scene ค่าเก่าแล้วทับสิ่งที่ผู้ใช้พิมพ์)
 */
export const deriveSceneDescription = (imagePrompt?: string, stylePrompt?: string): string => {
  const prompt = (imagePrompt || "").trim();
  const style = (stylePrompt || "").trim();
  if (!prompt || !style) return prompt;
  if (prompt.startsWith(style)) return prompt.slice(style.length).trim();
  if (prompt.endsWith(style)) return prompt.slice(0, prompt.length - style.length).trim();
  return prompt;
};

/**
 * script ที่จะส่งขึ้น backend — คัดเฉพาะ field ที่ backend ใช้ และ **ไม่ส่ง key ที่ยังไม่ได้เลือก**
 * (backend จะใช้ค่า default ของตัวเองแทน) จึงมี conditional spread เยอะ
 */
export const buildCleanedScript = (
  script: FinalScriptData,
  opts: CleanScriptOptions
): FinalScriptData => ({
  ...script,
  audio_only: opts.audioOnly,
  items: script.items.map((item) => {
    const voice = synchronizeVoicePrompt(item);
    return {
      id: item.id,
      headline: item.headline || "",
      script: voice.script,
      voice_guide: voice.voice_guide,
      voice_script: voice.voice_script,
      voice_name: item.voice_name || DEFAULT_VOICE_NAME,
      // ส่งค่า derived ไม่ใช่ค่าที่ state เก็บไว้: ไม่งั้น scene ที่เพิ่งแก้จะหายไปแล้ว run ก็ reuse รูปเดิม
      image_prompt: composeImagePrompt(item),
      active: item.active !== false, // true = enable scene, false = disable scene
      use_animated: opts.useAnimated[item.id] ?? false,
      ...(item.anim_prompt?.trim() && {
        is_animated: true,
        anim_prompt: item.anim_prompt,
        anim_mode: item.anim_mode || "single",
        ...(item.character_id != null && { character_id: item.character_id }),
      }),
      ...(item.is_uploaded && {
        is_uploaded: true,
        uploaded_video_url: item.uploaded_video_url || "",
      }),
      ...(item.is_image_uploaded && {
        is_image_uploaded: true,
        uploaded_image_url: item.uploaded_image_url || "",
      }),
      ...(item.text_overlays && item.text_overlays.length > 0 && { text_overlays: item.text_overlays }),
      ...(item.image_overlays && item.image_overlays.length > 0 && { image_overlays: item.image_overlays }),
      image_style: item.image_style || "",
      image_style_prompt: item.image_style_prompt || "",
      scene_description: item.scene_description || "",
      ken_burns_direction: item.ken_burns_direction || "off",
      ...(!opts.useGlobalPadding && item.padding_start != null && { padding_start: item.padding_start }),
      ...(!opts.useGlobalPadding && item.padding_end != null && { padding_end: item.padding_end }),
      // ไม่ได้เลือก = ไม่ส่ง key ให้หลังบ้านใช้ default ของตัวเอง
      // (หลังบ้าน hash "" เท่ากับไม่ส่ง key ⇒ signature เท่าเดิม ไม่มีสื่อเก่า regen)
      ...(item.image_model && { image_model: item.image_model }),
      ...(item.video_model && { video_model: item.video_model }),
      ...(item.reference_images && item.reference_images.length > 0 && { reference_images: item.reference_images }),
      ...(item.video_audio_mode && { video_audio_mode: item.video_audio_mode }),
    };
  }),
});

/**
 * รวม script ที่ได้จาก poll เข้ากับของที่อยู่บนจอ
 *
 * - ค่าที่ incoming ส่งมาว่าง แต่ของเดิมมี → ใช้ของเดิม (backend บาง step ยังไม่เติมค่ากลับ)
 * - `preserveEdits` (ผู้ใช้กำลังแก้ค้างยังไม่ save) → field ที่ผู้ใช้แก้ได้ใช้ของเดิม
 *   **ยกเว้น `voice_name` / `image_style` / `image_style_prompt` ที่ใช้ `||`** ⇒ ถ้าผู้ใช้ล้างค่าจนว่าง
 *   ค่าจาก incoming จะถูกใช้แทน (ไม่ใช่ค่าว่างของผู้ใช้) — ดูเทสต์ "field ที่ใช้ || …" ใน scriptPayload.test.ts
 *   ส่วน asset ที่ server สร้าง (image_path / audio_path / *_url) ยังรับจาก incoming ตามปกติ
 * - scene ที่ไม่มีของเดิม (id ใหม่) ใช้ incoming ทั้งก้อน
 */
export const mergeScriptFromPoll = (
  prev: FinalScriptData | null | undefined,
  incoming: FinalScriptData,
  preserveEdits: boolean
): FinalScriptData => {
  const prevMap: Record<string, ScriptWithImageItem> = {};
  (prev?.items || []).forEach((item) => {
    prevMap[item.id] = item;
  });

  return {
    ...incoming,
    items: incoming.items.map((rawIncomingItem) => {
      const incomingItem = hydrateVoicePrompt(rawIncomingItem);
      const prevItem = prevMap[incomingItem.id];
      if (!prevItem) return incomingItem;

      const base = {
        ...incomingItem,
        voice_guide: incomingItem.voice_guide ?? prevItem.voice_guide ?? "",
        voice_script: incomingItem.voice_script ?? prevItem.voice_script ?? "",
        active: incomingItem.active ?? prevItem.active ?? true,
        voice_name: incomingItem.voice_name || prevItem.voice_name || DEFAULT_VOICE_NAME,
        image_style: incomingItem.image_style || prevItem.image_style || "",
        image_style_prompt: incomingItem.image_style_prompt?.trim()
          ? incomingItem.image_style_prompt
          : prevItem.image_style_prompt || "",
        anim_prompt: incomingItem.anim_prompt || prevItem.anim_prompt || "",
        anim_mode: incomingItem.anim_mode || prevItem.anim_mode || "single",
      };

      if (!preserveEdits) return base;

      return {
        ...base,
        headline: prevItem.headline ?? base.headline,
        script: prevItem.script ?? base.script,
        voice_guide: prevItem.voice_guide ?? base.voice_guide,
        voice_script: prevItem.voice_script ?? base.voice_script,
        image_prompt: prevItem.image_prompt ?? base.image_prompt,
        scene_description: prevItem.scene_description ?? base.scene_description,
        voice_name: prevItem.voice_name || base.voice_name,
        image_style: prevItem.image_style || base.image_style,
        image_style_prompt: prevItem.image_style_prompt || base.image_style_prompt,
        ken_burns_direction: prevItem.ken_burns_direction ?? base.ken_burns_direction,
        text_overlays: prevItem.text_overlays ?? base.text_overlays,
        image_overlays: prevItem.image_overlays ?? base.image_overlays,
        anim_prompt: prevItem.anim_prompt ?? base.anim_prompt,
        anim_mode: prevItem.anim_mode ?? base.anim_mode,
        active: prevItem.active ?? base.active,
        character_id: prevItem.character_id ?? base.character_id,
        video_model: prevItem.video_model ?? base.video_model,
        padding_start: prevItem.padding_start ?? base.padding_start,
        padding_end: prevItem.padding_end ?? base.padding_end,
        image_model: prevItem.image_model ?? base.image_model,
        reference_images: prevItem.reference_images ?? base.reference_images,
      };
    }),
  };
};

export type RunNameSources = {
  /** ชื่อที่ผู้ใช้เพิ่งแก้บนจอ (ชนะทุกอย่าง) */
  titleOverride?: string | null;
  /** ชื่อจาก final_script ของ graph state */
  finalScriptTitle?: string | null;
  /** ชื่อจาก raw_scripts_images (มี 2 field ตามรุ่นของ backend) */
  rawTitlePodcast?: string | null;
  rawTitle?: string | null;
  /** ชื่อที่ตั้งไว้สำหรับ YouTube */
  youtubeTitle?: string | null;
  /** ชื่อในฟอร์มที่ผู้ใช้กรอกตอนสร้าง */
  directTitle?: string | null;
  /** ชื่อที่มาจาก run list */
  runTitle?: string | null;
  threadId?: string | null;
};

/**
 * ชื่อ run สำหรับ breadcrumb/หัวข้อ — ไล่ลำดับเดียวกับ title ใน run list ของ backend (`/runs`)
 * ค่าที่เป็นช่องว่างล้วนถือว่าไม่มี — **ยกเว้น `titleOverride` ที่ไม่ถูก trim** (ผู้ใช้พิมพ์อะไรก็ใช้ตามนั้น)
 * ถ้าไม่มีเลยใช้ `Script <8 ตัวแรกของ threadId ตัวใหญ่>`
 * และถ้าไม่มีแม้ threadId (ยังไม่สร้าง run) ใช้ชื่อโมดูล
 */
export const resolveRunName = (src: RunNameSources): string => {
  if (src.titleOverride) return src.titleOverride;

  const resolved =
    src.finalScriptTitle?.trim() ||
    src.rawTitlePodcast?.trim() ||
    src.rawTitle?.trim() ||
    src.youtubeTitle?.trim() ||
    src.directTitle?.trim() ||
    src.runTitle?.trim();
  if (resolved) return resolved;

  return src.threadId ? `Script ${src.threadId.slice(0, 8).toUpperCase()}` : "Prompt Kits";
};
